import * as THREE from 'three';
import { getFabVisualConfig } from './fabVisualConfig';

export type Canvas3DGlSettings = {
  antialias: boolean;
  alpha: boolean;
  powerPreference: WebGLPowerPreference;
  stencil: boolean;
  depth: boolean;
  logarithmicDepthBuffer: boolean;
  preserveDrawingBuffer: boolean;
  premultipliedAlpha: boolean;
  failIfMajorPerformanceCaveat: boolean;
  desynchronized: boolean;
};

export function isWindowsGpuHost(
  userAgent = typeof navigator === 'undefined' ? '' : navigator.userAgent,
  platform = typeof navigator === 'undefined' ? '' : navigator.platform
): boolean {
  return /Win/i.test(platform) || /Windows/i.test(userAgent);
}

export function getCanvas3DGlSettings(windows = isWindowsGpuHost()): Canvas3DGlSettings {
  return {
    antialias: true,
    alpha: false,
    powerPreference: 'high-performance',
    stencil: false,
    depth: true,
    logarithmicDepthBuffer: !windows,
    preserveDrawingBuffer: false,
    premultipliedAlpha: false,
    failIfMajorPerformanceCaveat: false,
    desynchronized: windows,
  };
}

export function getCanvas3DDprCap(
  simulationRunning: boolean,
  elementCount: number,
  windows = isWindowsGpuHost()
): number {
  // Live sim redraws every frame — cap fill rate so pan stays responsive.
  if (simulationRunning) return windows ? 1 : elementCount > 400 ? 1.25 : 1.5;
  // Native DPR keeps edges crisp on HiDPI laptop panels (common Windows blur cause).
  if (windows) return elementCount > 400 ? 1.25 : 1.5;
  return elementCount > 80 ? 1.5 : 1.75;
}

/** DPR while simulation is running (pan/zoom must stay smooth). */
export function getCanvas3DSimulationDpr(windows = isWindowsGpuHost()): number {
  return windows ? 1 : 1.25;
}

export function getCanvas3DToneMapping(): THREE.ToneMapping {
  return THREE.ACESFilmicToneMapping;
}

export function getCanvas3DToneMappingExposure(): number {
  return getFabVisualConfig().toneMappingExposure;
}

export function getCanvas3DLightBudget(windows = isWindowsGpuHost()): {
  extraFillLights: boolean;
  environmentIntensity: number;
} {
  return {
    extraFillLights: !windows,
    environmentIntensity: windows ? 0.72 : 0.82,
  };
}

export type Canvas3DRendererConfig = {
  outputColorSpace?: THREE.ColorSpace | string;
  sortObjects: boolean;
  shadowMap: { autoUpdate: boolean };
  info: { autoReset: boolean };
  domElement: { style: { imageRendering: string } };
};

/** Chrome/Edge expose `navigator.gpu`. The 3D view uses it when present. */
export function shouldPreferWebGPU(
  gpu: unknown = typeof navigator === 'undefined' ? undefined : navigator.gpu
): boolean {
  return gpu != null;
}

export async function requestWebGpuAdapter(
  gpu: GPU | undefined = typeof navigator === 'undefined' ? undefined : navigator.gpu
): Promise<GPUAdapter | null> {
  if (!gpu) return null;
  try {
    return await gpu.requestAdapter({ powerPreference: 'high-performance' });
  } catch {
    return null;
  }
}

export function isClassicWebGLRenderer(renderer: { isWebGLRenderer?: boolean }): boolean {
  return renderer.isWebGLRenderer === true;
}

export function isCanvas3DWebGPU(renderer: {
  isWebGPURenderer?: boolean;
  backend?: { isWebGPUBackend?: boolean };
}): boolean {
  return renderer.isWebGPURenderer === true && renderer.backend?.isWebGPUBackend === true;
}

export function isCanvas3DNodeRenderer(renderer: {
  isWebGPURenderer?: boolean;
}): boolean {
  return renderer.isWebGPURenderer === true;
}

export function configureCanvas3DRenderer(renderer: Canvas3DRendererConfig): void {
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.sortObjects = false;
  renderer.shadowMap.autoUpdate = false;
  renderer.info.autoReset = true;
  // Crisp canvas scaling — avoid browser smoothing a low-res backing store.
  renderer.domElement.style.imageRendering = 'auto';
}

/**
 * Creates a configured WebGLRenderer with high-performance Windows/cross-platform settings.
 * Retains WebGL state required by PMREMGenerator and postprocessing.
 */
export function createCanvas3DRenderer(
  props: THREE.WebGLRendererParameters
): THREE.WebGLRenderer {
  const settings = getCanvas3DGlSettings();
  const renderer = new THREE.WebGLRenderer({
    canvas: props.canvas as HTMLCanvasElement,
    ...settings,
  });
  configureCanvas3DRenderer(renderer);
  return renderer;
}

